import React, {useCallback, useEffect, useRef, useState} from "react";

import AuthService from "../../services/auth.service";
import {Card} from "react-bootstrap";
import {Link} from "react-router-dom";
import {useNavigate} from "react-router";
import {focusOn, REQUIRED, TOTP} from "../canopia/CanopiaUtils";
import {Trans, useTranslation} from "react-i18next";
import Button from "react-bootstrap/esm/Button";
import SixDigitCode from "./SixDigitCode";
import MessageBox from "../core/MessageBox";

export default function Login(props) {

    const navigate = useNavigate();
    const {t} = useTranslation();

    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [errorName, setErrorName] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [mfaType, setMfaType] = useState(null);
    const [mfaCode, setMfaCode] = useState(null);

    const continueRef = useRef(null);

    const redirect = useCallback(() => {
        // Forward to the portfolio after a successful login
        // TODO: Back to the root, should get the from location
        // this.props.history.replace(this.state.from);
        navigate("/portfolio");
    }, [navigate]);

    useEffect(() => {
        const currentUser = AuthService.getCurrentUser();
        if (currentUser) {
            redirect();
        }
    }, [redirect]);

    const handleLogin = (e) => {
        e.preventDefault();

        setLoading(true);
        setErrorName(null);
        setErrorMessage(null);

        let valid = true;
        [username, password].forEach(prop => {
            if (!prop || prop.trim().length === 0) {
                valid = false;
                setErrorName('Please fill in all the required fields');
            }
        });

        if (valid) {
            AuthService.login(username, password)
                .then((response) => {
                        const mfaResponse = requiresMfa(response);
                        if (mfaResponse && mfaResponse.requiresMfa) {
                            const mfaType = mfaResponse.mfaType;
                            setMfaType(mfaType);
                        } else {
                            AuthService.saveUser(response);
                            redirect();
                        }
                    },
                    error => {
                        processError(error);
                    }
                );
        }
        setLoading(false);
    }

    function requiresMfa(response) {
        if (response.data.requiresMfa) {
            return response.data;
        }
        return null;
    }

    const handleMfaLogin = () => {
        setLoading(true);
        setErrorName(null);
        setErrorMessage(null);

        AuthService.loginMfa(username, password, mfaCode)
            .then(() => {
                    redirect();
                },
                error => {
                    setLoading(false);
                    processError(error);
                }
            );
    }

    const handleMfaLoginMouse = (e) => {
        e.preventDefault();
        handleMfaLogin();
    };

    const processError = (error) => {
        let response = error.response;

        let errorName;
        let errorMessage;
        if (response
            && response.data
            && response.data.error
            && response.data.message
        ) {
            errorName = response.data.error;
            errorMessage = response.data.message;
        } else {
            errorName = 'Service unavailable';
            errorMessage = 'We apologize for the inconvenience, our team is working on solving the issue. Please try to come back in a few minutes.\nThank you for your patience.';
            if (error.message) {
                console.log(error.message);
            }
        }

        setErrorName(errorName);
        setErrorMessage(errorMessage);
    }

    function hideMfa() {
        setMfaType(null);
    }

    const completeMfaCode = (code) => {
        setMfaCode(code);
        focusOn(continueRef);
    }

    return (
        <div className="main-content">
            {props.authMessage && (
                <Card style={{width: '50rem', marginLeft: 'auto', marginRight: 'auto', marginBottom: '20px'}}>
                    <Card.Body>
                        <header className="jumbotron">
                            <h3 className='canopia'>Information</h3>
                            <p>{props.authMessage}</p>
                        </header>
                    </Card.Body>
                </Card>
            )}
            <Card className={'canopia-card'} style={{width: '35rem'}}>
                <Card.Body>
                    <Card.Title className='canopia-card-title'>{t('login')}</Card.Title>
                    {!mfaType ?
                        <form onSubmit={handleLogin}>
                            <div className="form-group">
                                <label htmlFor="username">E-mail</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="username"
                                    value={username}
                                    placeholder={REQUIRED}
                                    autoComplete="username"
                                    onChange={(e) => setUsername(e.target.value)}
                                />
                            </div>
                            <br/>

                            <div className="form-group">
                                <label htmlFor="password">{t('pwd')}</label>
                                <input
                                    type="password"
                                    className="form-control"
                                    name="password"
                                    value={password}
                                    placeholder={REQUIRED}
                                    autoComplete="current-password"
                                    onChange={(e) => setPassword(e.target.value)}
                                />
                            </div>
                            <br/>

                            <div className="form-group">
                                <Link to={'/request-reset-pwd'} className={'std-link'}>
                                    <span>{t('forgotPwd')}</span>
                                </Link>
                            </div>
                            <br/>

                            <div className="form-group">
                                <button className="btn btn-primary btn-block" disabled={loading}>
                                    {loading && (
                                        <span className="spinner-border spinner-border-sm"
                                              style={{marginRight: '5px'}}/>
                                    )}
                                    <span>{t('login')}</span>
                                </button>
                            </div>
                        </form>
                        : <>
                            <div className={'flex-nowrap'} style={{marginTop: '1rem'}}>
                                <div className={"setup-th"} style={{flexBasis: '100%', padding: '0.55em 0.5em'}}>
                                    <Trans i18nKey={mfaType === TOTP ? "TOTP_Code_Enter_text" : "EMAIL_Code_Enter_text"}
                                           components={{p: <p/>, header: <p className="header"/>}}/>
                                </div>
                            </div>
                            <div className={'flex-nowrap'} style={{marginTop: '1rem', alignItems: 'center'}}>
                                <SixDigitCode
                                    onChange={(code) => setMfaCode(code)}
                                    onComplete={(code) => completeMfaCode(code)}
                                    disabled={false}
                                />
                            </div>
                            <div className={'flex-nowrap'} style={{marginTop: '1rem'}}>
                                <div className={"setup-th"} style={{padding: '0.55em 0.5em 0.55em 0'}}>
                                    <Button variant="secondary" onClick={() => hideMfa()}>
                                        {t('cancel')}
                                    </Button>
                                </div>
                                <div className={"setup-th"} style={{padding: '0.55em 0.5em 0.55em 0'}}>
                                    <Button variant="primary"
                                            ref={el => continueRef.current = el}
                                            onClick={(e) => handleMfaLoginMouse(e)}
                                            disabled={!(mfaCode && mfaCode.length === 6)}>
                                        {t('continue')}
                                    </Button>
                                </div>
                            </div>
                        </>
                    }
                    {errorName && (
                        <MessageBox
                            title={errorName}
                            message={errorMessage}
                            colorClass={'canopia6'}
                            autoDismiss={10000}  // Auto-dismiss after 10 seconds
                            onDismiss={() => {
                                setErrorMessage(null);
                                setErrorName(null)
                            }}
                        />
                    )}
                </Card.Body>
            </Card>
        </div>
    );
}
